import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import openSocket from "../../services/socket-io";
import { AuthContext } from "../Auth/AuthContext";
import { playNotificationSound } from "../../services/notificationSound";

const InternalChatNotificationsContext = createContext();

const InternalChatNotificationsProvider = ({ children }) => {
  const { user } = useContext(AuthContext);
  const history = useHistory();
  const location = useLocation();
  const [unreadByConversation, setUnreadByConversation] = useState({});
  const activeConversationIdRef = useRef(null);
  const recentMessageIdsRef = useRef(new Set());
  const canUseDesktopNotifications =
    typeof window !== "undefined" &&
    "Notification" in window &&
    window.isSecureContext;

  const activeConversationId = useMemo(() => {
    const match = location.pathname.match(/^\/internal-chat\/(\d+)/);
    return match ? Number(match[1]) : null;
  }, [location.pathname]);

  useEffect(() => {
    activeConversationIdRef.current = activeConversationId;
  }, [activeConversationId]);

  const markConversationRead = conversationId => {
    if (!conversationId) {
      return;
    }

    setUnreadByConversation(prevState => {
      if (!prevState[conversationId]) {
        return prevState;
      }

      const nextState = { ...prevState };
      delete nextState[conversationId];
      return nextState;
    });
  };

  const incrementConversationUnread = conversationId => {
    if (!conversationId) {
      return;
    }

    setUnreadByConversation(prevState => ({
      ...prevState,
      [conversationId]: (prevState[conversationId] || 0) + 1
    }));
  };

  useEffect(() => {
    if (canUseDesktopNotifications && Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, [canUseDesktopNotifications]);

  useEffect(() => {
    if (activeConversationId && document.visibilityState === "visible") {
      markConversationRead(activeConversationId);
    }
  }, [activeConversationId]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (
        document.visibilityState === "visible" &&
        activeConversationIdRef.current
      ) {
        markConversationRead(activeConversationIdRef.current);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    if (!user?.id) {
      return undefined;
    }

    const socket = openSocket();

    const hasAlreadyProcessedMessage = messageId => {
      const normalizedMessageId = Number.isFinite(Number(messageId))
        ? Number(messageId)
        : String(messageId || "");

      if (!normalizedMessageId) {
        return false;
      }

      if (recentMessageIdsRef.current.has(normalizedMessageId)) {
        return true;
      }

      recentMessageIdsRef.current.add(normalizedMessageId);
      setTimeout(() => {
        recentMessageIdsRef.current.delete(normalizedMessageId);
      }, 5000);

      return false;
    };

    socket.on("internalChat:message", payload => {
      const message = payload?.message;
      const conversation = payload?.conversation;

      if (!message || !conversation) {
        return;
      }

      if (
        Array.isArray(conversation.participantUserIds) &&
        !conversation.participantUserIds
          .map(Number)
          .includes(Number(user.id))
      ) {
        return;
      }

      if (Number(message.userId) === Number(user.id)) {
        return;
      }

      if (hasAlreadyProcessedMessage(message.id)) {
        return;
      }

      const conversationId = Number(conversation.id);
      const isActiveConversation =
        conversationId === Number(activeConversationIdRef.current) &&
        document.visibilityState === "visible";

      if (isActiveConversation) {
        markConversationRead(conversationId);
        return;
      }

      incrementConversationUnread(conversationId);

      const title =
        conversation.type === "group"
          ? conversation.name || "Grupo interno"
          : (conversation.participants || [])
              .map(participant => participant.user)
              .find(participantUser => Number(participantUser?.id) !== Number(user.id))
              ?.name || "Chat interno";

      toast.info(`Nova mensagem interna em ${title}`);
      void playNotificationSound("internalChat");

      if (canUseDesktopNotifications && Notification.permission === "granted") {
        const notification = new Notification(`Chat interno: ${title}`, {
          body: String(message.body || "").slice(0, 160),
          tag: `internal-chat-${conversationId}`
        });

        notification.onclick = event => {
          event.preventDefault();
          window.focus();
          history.push(`/internal-chat/${conversationId}`);
        };
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [canUseDesktopNotifications, history, user?.id]);

  const unreadCount = Object.values(unreadByConversation).reduce(
    (total, value) => total + Number(value || 0),
    0
  );

  return (
    <InternalChatNotificationsContext.Provider
      value={{
        unreadCount,
        unreadByConversation,
        markConversationRead
      }}
    >
      {children}
    </InternalChatNotificationsContext.Provider>
  );
};

export {
  InternalChatNotificationsContext,
  InternalChatNotificationsProvider
};
